import React, { useState, useEffect } from 'react';
import {
    Megaphone,
    Plus,
    Edit,
    Trash2,
    Eye,
    EyeOff,
    Loader2,
    AlertTriangle,
    RefreshCw,
    Search,
    X,
    Save,
    Image as ImageIcon,
    Calendar
} from 'lucide-react';
import { sanitizeURL } from '../utils/sanitizer';

const AnnouncementsPage = () => {
    const [announcements, setAnnouncements] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterPublic, setFilterPublic] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
    const [currentAnnouncement, setCurrentAnnouncement] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [imageErrors, setImageErrors] = useState({}); // Track failed images

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    // Form state
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        image_url: '',
        is_public: false
    });

    useEffect(() => {
        fetchAnnouncements();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, filterPublic]);

    const fetchAnnouncements = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const accessToken = localStorage.getItem('accessToken');
            let url = `https://api.digitalaalim.com/admin/announcements?page=${currentPage}&page_size=${pageSize}`;

            if (filterPublic !== '') {
                url += `&is_public=${filterPublic}`;
            }

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch announcements: ${response.statusText}`);
            }

            const data = await response.json();
            setAnnouncements(data.items || []);
            setTotalItems(data.total || 0);
            setTotalPages(data.total_pages || 1);
            setCurrentPage(data.page || 1);
        } catch (error) {
            console.error('Error fetching announcements:', error);
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const openCreateModal = () => {
        setModalMode('create');
        setFormData({
            title: '',
            description: '',
            image_url: '',
            is_public: false
        });
        setCurrentAnnouncement(null);
        setShowModal(true);
    };

    const openEditModal = (announcement) => {
        setModalMode('edit');
        setFormData({
            title: announcement.title,
            description: announcement.description,
            image_url: announcement.image_url || '',
            is_public: announcement.is_public
        });
        setCurrentAnnouncement(announcement);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setFormData({
            title: '',
            description: '',
            image_url: '',
            is_public: false
        });
        setCurrentAnnouncement(null);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);

        try {
            const accessToken = localStorage.getItem('accessToken');
            const payload = {
                title: formData.title.trim(),
                description: formData.description.trim(),
                image_url: formData.image_url.trim() || null,
                is_public: formData.is_public
            };

            let response;
            if (modalMode === 'create') {
                response = await fetch('https://api.digitalaalim.com/admin/announcements', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload)
                });
            } else {
                response = await fetch(`https://api.digitalaalim.com/admin/announcements/${currentAnnouncement._id}`, {
                    method: 'PATCH',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload)
                });
            }

            if (!response.ok) {
                throw new Error(`Failed to ${modalMode} announcement: ${response.statusText}`);
            }

            alert(`Announcement ${modalMode === 'create' ? 'created' : 'updated'} successfully!`);
            closeModal();
            fetchAnnouncements();
        } catch (error) {
            console.error(`Error ${modalMode}ing announcement:`, error);
            alert(`Failed to ${modalMode} announcement: ${error.message}`);
        } finally {
            setIsSaving(false);
        }
    };

    const handlePublish = async (announcementId) => {
        const confirmed = window.confirm('Are you sure you want to publish this announcement? This will unpublish any currently public announcement.');
        if (!confirmed) return;

        try {
            const accessToken = localStorage.getItem('accessToken');
            const response = await fetch(`https://api.digitalaalim.com/admin/announcements/${announcementId}/publish`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to publish announcement: ${response.statusText}`);
            }

            alert('Announcement published successfully!');
            fetchAnnouncements();
        } catch (error) {
            console.error('Error publishing announcement:', error);
            alert(`Failed to publish announcement: ${error.message}`);
        }
    };

    const handleUnpublish = async (announcementId) => {
        const confirmed = window.confirm('Are you sure you want to unpublish this announcement?');
        if (!confirmed) return;

        try {
            const accessToken = localStorage.getItem('accessToken');
            const response = await fetch(`https://api.digitalaalim.com/admin/announcements/${announcementId}/unpublish`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to unpublish announcement: ${response.statusText}`);
            }

            alert('Announcement unpublished successfully!');
            fetchAnnouncements();
        } catch (error) {
            console.error('Error unpublishing announcement:', error);
            alert(`Failed to unpublish announcement: ${error.message}`);
        }
    };

    const handleDelete = async (announcementId) => {
        const confirmed = window.confirm('Are you sure you want to delete this announcement? This action cannot be undone.');
        if (!confirmed) return;

        try {
            const accessToken = localStorage.getItem('accessToken');
            const response = await fetch(`https://api.digitalaalim.com/admin/announcements/${announcementId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to delete announcement: ${response.statusText}`);
            }

            alert('Announcement deleted successfully!');
            fetchAnnouncements();
        } catch (error) {
            console.error('Error deleting announcement:', error);
            alert(`Failed to delete announcement: ${error.message}`);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const filteredAnnouncements = announcements.filter(announcement =>
        announcement.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        announcement.description.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-8">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                    <Megaphone className="text-blue-600" size={32} />
                    Announcements
                </h1>
                <p className="mt-2 text-gray-600">
                    Create and manage announcements for Digital Aalim users
                </p>
            </div>

            {/* Controls */}
            <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                    <div className="relative w-full md:w-80">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search size={16} className="text-gray-400" />
                        </div>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search announcements..."
                            className="pl-10 w-full border border-gray-300 rounded-md py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div className="flex items-center gap-2 w-full md:w-auto">
                        <select
                            value={filterPublic}
                            onChange={(e) => setFilterPublic(e.target.value)}
                            className="text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">All Status</option>
                            <option value="true">Published</option>
                            <option value="false">Unpublished</option>
                        </select>

                        <button
                            onClick={fetchAnnouncements}
                            className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
                        >
                            <RefreshCw size={14} className="mr-1" />
                            Refresh
                        </button>

                        <button
                            onClick={openCreateModal}
                            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                        >
                            <Plus size={16} className="mr-1" />
                            Create Announcement
                        </button>
                    </div>
                </div>
            </div>

            {/* Announcements List */}
            {isLoading ? (
                <div className="flex justify-center items-center h-64">
                    <Loader2 size={40} className="animate-spin text-blue-600" />
                </div>
            ) : error ? (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                    <div className="flex items-center">
                        <AlertTriangle size={24} className="text-red-500 mr-3" />
                        <div>
                            <h3 className="text-red-800 font-medium">Error loading announcements</h3>
                            <p className="text-red-700">{error}</p>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="space-y-4">
                    {filteredAnnouncements.length > 0 ? (
                        filteredAnnouncements.map((announcement) => (
                            <div
                                key={announcement._id}
                                className={`bg-white rounded-xl shadow-sm overflow-hidden border-l-4 hover:shadow-md transition-shadow ${announcement.is_public ? 'border-green-500' : 'border-gray-300'
                                    }`}
                            >
                                <div className="p-6">
                                    {/* Header with title and status */}
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <h3 className="text-2xl font-bold text-gray-900">
                                                    {announcement.title}
                                                </h3>
                                                {announcement.is_public && (
                                                    <span className="inline-flex items-center px-3 py-1 text-xs font-semibold text-green-800 bg-green-100 rounded-full">
                                                        <Eye size={12} className="mr-1" />
                                                        Published
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Content area with image and description */}
                                    <div className="grid md:grid-cols-3 gap-6 mb-4">
                                        {/* Image section */}
                                        {announcement.image_url && (
                                            <div className="md:col-span-1">
                                                <div className="relative rounded-lg overflow-hidden border-2 border-gray-200 bg-gray-50">
                                                    {!imageErrors[announcement._id] ? (
                                                        <img
                                                            src={sanitizeURL(announcement.image_url)}
                                                            alt={announcement.title}
                                                            className="w-full h-auto object-cover"
                                                            onError={() => {
                                                                setImageErrors(prev => ({
                                                                    ...prev,
                                                                    [announcement._id]: true
                                                                }));
                                                            }}
                                                        />
                                                    ) : (
                                                        <div className="flex items-center justify-center h-48 text-gray-400">
                                                            <div className="text-center">
                                                                <ImageIcon className="mx-auto h-12 w-12 mb-2" />
                                                                <p className="text-sm">Image not available</p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Description section */}
                                        <div className={announcement.image_url ? 'md:col-span-2' : 'md:col-span-3'}>
                                            <div className="prose prose-sm max-w-none">
                                                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap text-base">
                                                    {announcement.description}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Footer with metadata and actions */}
                                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-gray-100">
                                        <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                                            <div className="flex items-center gap-1.5">
                                                <Calendar size={14} className="text-gray-400" />
                                                <span>Created: {formatDate(announcement.created_at)}</span>
                                            </div>
                                            <div className="flex items-center gap-1.5">
                                                <Calendar size={14} className="text-gray-400" />
                                                <span>Updated: {formatDate(announcement.updated_at)}</span>
                                            </div>
                                        </div>

                                        <div className="flex flex-wrap items-center gap-2">
                                            {announcement.is_public ? (
                                                <button
                                                    onClick={() => handleUnpublish(announcement._id)}
                                                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-orange-700 bg-orange-100 rounded-lg hover:bg-orange-200 transition-colors"
                                                    title="Unpublish"
                                                >
                                                    <EyeOff size={16} className="mr-1.5" />
                                                    Unpublish
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() => handlePublish(announcement._id)}
                                                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-green-700 bg-green-100 rounded-lg hover:bg-green-200 transition-colors"
                                                    title="Publish"
                                                >
                                                    <Eye size={16} className="mr-1.5" />
                                                    Publish
                                                </button>
                                            )}

                                            <button
                                                onClick={() => openEditModal(announcement)}
                                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors"
                                                title="Edit"
                                            >
                                                <Edit size={16} className="mr-1.5" />
                                                Edit
                                            </button>

                                            <button
                                                onClick={() => handleDelete(announcement._id)}
                                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-700 bg-red-100 rounded-lg hover:bg-red-200 transition-colors"
                                                title="Delete"
                                            >
                                                <Trash2 size={16} className="mr-1.5" />
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                            <Megaphone size={48} className="mx-auto text-gray-400 mb-4" />
                            <h3 className="text-lg font-medium text-gray-800 mb-2">
                                {searchTerm || filterPublic ? 'No announcements found' : 'No announcements yet'}
                            </h3>
                            <p className="text-gray-600 mb-4">
                                {searchTerm || filterPublic
                                    ? 'Try adjusting your search or filters'
                                    : 'Create your first announcement to get started'}
                            </p>
                            {!searchTerm && !filterPublic && (
                                <button
                                    onClick={openCreateModal}
                                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                                >
                                    <Plus size={16} className="mr-1" />
                                    Create Announcement
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between bg-white rounded-xl shadow-sm p-4">
                    <div className="text-sm text-gray-600">
                        Showing page {currentPage} of {totalPages} ({totalItems} total)
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="px-3 py-1 text-sm border rounded-md disabled:opacity-50 hover:bg-gray-50"
                        >
                            Previous
                        </button>
                        <button
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            className="px-3 py-1 text-sm border rounded-md disabled:opacity-50 hover:bg-gray-50"
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}

            {/* Create/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
                            <h2 className="text-xl font-bold text-gray-800">
                                {modalMode === 'create' ? 'Create New Announcement' : 'Edit Announcement'}
                            </h2>
                            <button
                                onClick={closeModal}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Title <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleInputChange}
                                    required
                                    maxLength={200}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter announcement title"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Description <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    required
                                    maxLength={5000}
                                    rows={6}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter announcement description"
                                />
                                <div className="text-xs text-gray-500 mt-1">
                                    {formData.description.length} / 5000 characters
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Image URL (Optional)
                                </label>
                                <div className="flex items-center gap-2">
                                    <ImageIcon size={20} className="text-gray-400" />
                                    <input
                                        type="url"
                                        name="image_url"
                                        value={formData.image_url}
                                        onChange={handleInputChange}
                                        className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="https://example.com/image.jpg"
                                    />
                                </div>
                                {formData.image_url && (
                                    <div className="mt-2">
                                        <img
                                            src={formData.image_url}
                                            alt="Preview"
                                            className="max-w-xs rounded-lg border border-gray-200"
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="flex items-center gap-2 p-4 bg-blue-50 rounded-lg">
                                <input
                                    type="checkbox"
                                    id="is_public"
                                    name="is_public"
                                    checked={formData.is_public}
                                    onChange={handleInputChange}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label htmlFor="is_public" className="text-sm font-medium text-gray-700">
                                    Publish immediately (will unpublish any currently public announcement)
                                </label>
                            </div>

                            <div className="flex items-center justify-end gap-3 pt-4 border-t">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSaving}
                                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                                >
                                    {isSaving ? (
                                        <>
                                            <Loader2 size={16} className="mr-2 animate-spin" />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <Save size={16} className="mr-2" />
                                            {modalMode === 'create' ? 'Create' : 'Update'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AnnouncementsPage;
